import { Loader2, Square, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useVoicePreview } from '@/hooks/use-voice-preview';

/**
 * A speaker button that plays a line in the voice agent's voice (ADR-344): the
 * spoken name on the providers screen, the spoken address on the locations
 * screen. Plays exactly `text` — in an editor, what is in the box, unsaved.
 */
export function PlaySpokenButton({
  text,
  label,
}: {
  text: string;
  /** What is played, for screen readers, e.g. "spoken name". */
  label: string;
}): React.ReactElement {
  const { toggle, loading, playing } = useVoicePreview(text);
  const empty = text.trim() === '';

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="shrink-0 text-muted-foreground hover:text-foreground"
            disabled={empty || loading}
            aria-label={playing ? `Stop the ${label}` : `Play the ${label}`}
            onClick={toggle}
          >
            {loading ? <Loader2 className="animate-spin" /> : playing ? <Square /> : <Volume2 />}
          </Button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {playing
            ? 'Stop'
            : 'Hear how the voice agent says it. Close to how it sounds on a call, not identical.'}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
